import { makeStyles } from '@material-ui/core';
import clsx from 'clsx';
import { useFormikContext } from 'formik';
import { isString } from 'lodash';
import React, { useCallback, useContext } from 'react';
import { useDropzone } from 'react-dropzone-latest';
import { v4 } from 'uuid';

import { notify } from 'src/clients/ApiService';
import { PortalConfigContext } from 'src/context';
import { useProgressUpload } from 'src/hooks/useProgressUpload';
import { useAppDispatch } from 'src/hooks/useStore';
import Button from 'src/legacy/components/Button';
import { DropzoneFilesIcon, TrashIcon } from 'src/legacy/components/Icons';
import { ImageCropper } from 'src/legacy/components/ImageCropper/ImageCropper';
import { ModalWrapper } from 'src/legacy/components/Modals';
import {
  MAX_IMAGE_DIMENSION,
  MIN_IMAGE_DIMENSION,
} from 'src/legacy/components/Products/ProductEditPage/ProductEditPage';
import BaseTypography from 'src/legacy/components/Text/BaseTypography';
import {
  BlackHeadings,
  GraySmall,
  ModalUnderlay,
  NonHoverBorder,
  white,
} from 'src/theme/colors';
import { FileWithPreview } from 'src/types/files';
import { FileUtils, S3Utils } from 'src/utils';
import ImageUtils from 'src/utils/FileUtils';
import { UploadResponse } from 'src/utils/S3Utils';

const useStyles = makeStyles(() => ({
  dropzone: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px dashed #ccc',
    padding: 24,
    gap: 16,
    borderRadius: 4,
  },
  fileItemsWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    flexWrap: 'wrap',
  },
  createIcon: {
    borderColor: NonHoverBorder,
  },
  imagePreview: {
    position: 'relative',
    height: 60,
    width: 60,
    border: `1px solid ${NonHoverBorder}`,
    boxSizing: 'border-box',
    borderRadius: 4,
    '& img': {
      height: '100%',
      width: '100%',
      borderRadius: 4,
    },
    '&:hover [data-attrib=remove-button]': {
      display: 'flex', // when hovering over the image preview show image remove button
    },
  },
  removeButton: {
    position: 'absolute',
    inset: 0,
    height: '100%',
    width: '100%',
    display: 'none',
    alignItems: 'center',
    justifyContent: 'center',
    color: white,
    fontSize: 15,
    backgroundColor: ModalUnderlay,
    borderRadius: 4,
    border: 'none',
  },
  containedImage: {
    objectFit: 'contain',
  },
  cropperContainer: {
    height: 400,
  },
  dropzoneButton: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 12,
  },
}));

export type CroppedImage = {
  preview: string;
  name: string | undefined;
};

type Props = {
  imageUrl: string;
};

export const ProductFileDropzone = ({ imageUrl }: Props) => {
  const classes = useStyles();
  const [croppedImage, setCroppedImage] = React.useState<string>();
  const [chosenImage, setChosenImage] = React.useState<FileWithPreview>();
  const { setFieldValue } = useFormikContext();
  const { setUploadProgress, startUploadFiles } = useProgressUpload();
  const portalConfig = useContext(PortalConfigContext);
  const [isImageCropperOpen, setOpenImageCropperStatus] =
    React.useState<boolean>(false);

  const aspect = 500 / 500;
  const dispatch = useAppDispatch();

  const handleChangeFile = async (file: CroppedImage | null) => {
    if (!file) {
      setFieldValue('imageUrls', []);
      return;
    }

    const uploadImage = async () => {
      startUploadFiles();
      const randomImageId = v4();
      let imageFileUrl = '';

      try {
        const blob = await fetch(file.preview ?? '').then((r) => r.blob());
        const resizedImage = await ImageUtils.CompressImage(
          blob as File,
          512,
          512,
        );

        const res: UploadResponse = await S3Utils.uploadPortalFile(
          resizedImage,
          `${portalConfig.portalHeader}/images/products/${randomImageId}/${file?.name}`,
          (loaded, totalSize) => {
            setUploadProgress({ totalSize, loaded: [loaded] });
          },
        );

        if (res?.Location) {
          imageFileUrl = res.Location;
        }
      } catch {
        imageFileUrl = '';
      }

      setFieldValue('imageUrls', [imageFileUrl]);
    };

    await uploadImage();
  };

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const filesArray = Array.from(acceptedFiles);
    const file = filesArray.at(0);
    if (file) {
      const preview = URL.createObjectURL(file);
      const _chosenImage = Object.assign(file, { preview });
      setChosenImage(_chosenImage);
      setOpenImageCropperStatus(true);
    }
  }, []);

  const {
    getRootProps,
    getInputProps,
    open: openFilePicker,
  } = useDropzone({
    onDrop,
    noClick: true,
    accept: 'image/*',
  });

  const handleCropImage = () => {
    const croppedImgObject = {
      preview: croppedImage ?? '',
      name: chosenImage?.name,
    };
    handleChangeFile(croppedImgObject);
    setOpenImageCropperStatus(false);
  };

  const onCropSucceed = () => {
    const image = new Image();
    image.src = chosenImage?.preview ?? '';
    image.onload = async () => {
      const { width, height } = image;

      const isImageSmall =
        width < MIN_IMAGE_DIMENSION || height < MIN_IMAGE_DIMENSION;
      const isImageLarge =
        width > MAX_IMAGE_DIMENSION || height > MAX_IMAGE_DIMENSION;

      if (isImageSmall) {
        notify({
          status: 'error',
          errorMessage:
            'Image is too small. Recommended dimensions are 448x448 to 4096x4096',
          dispatch,
        });
      } else if (isImageLarge) {
        notify({
          status: 'error',
          errorMessage:
            'Image is too large. Recommended dimensions are 448x448 to 4096x4096',
          dispatch,
        });
      } else {
        handleCropImage();
      }
    };
  };

  return (
    <>
      <div {...getRootProps()}>
        {!imageUrl.length && (
          <div className={classes.dropzone}>
            <input {...getInputProps()} />
            <DropzoneFilesIcon style={{ fontSize: 60 }} />
            <div className={classes.dropzoneButton}>
              <Button
                htmlId="upload-question-files-button"
                variant="contained"
                color="secondary"
                onClick={openFilePicker}
                size="medium"
                progressStyles={{
                  color: BlackHeadings,
                }}
              >
                Choose an image
              </Button>
              <BaseTypography fontType="12Medium" textColor={GraySmall}>
                or drop a file here to upload
              </BaseTypography>
            </div>
          </div>
        )}
        <ModalWrapper
          noBorder
          title="Crop your image"
          onClose={() => {
            setOpenImageCropperStatus(false);
          }}
          onSuccess={onCropSucceed}
          open={isImageCropperOpen}
        >
          <div className={classes.cropperContainer}>
            {isImageCropperOpen && chosenImage && (
              <ImageCropper
                aspect={aspect}
                onCropComplete={async (_, croppedAreaPixels) => {
                  try {
                    if (chosenImage?.preview) {
                      const croppedImg = await FileUtils.getCroppedImg(
                        chosenImage.preview,
                        croppedAreaPixels,
                        0,
                      );
                      const isBlobString = isString(croppedImg);
                      if (isBlobString) {
                        setCroppedImage(croppedImg);
                      }
                    }
                  } catch (e) {
                    console.warn(e);
                  }
                }}
                image={chosenImage}
              />
            )}
          </div>
        </ModalWrapper>
      </div>
      <div className={classes.fileItemsWrapper}>
        <input {...getInputProps()} />
        {imageUrl && (
          <div className={classes.imagePreview} data-testid="image-preview">
            <img
              src={imageUrl}
              alt="product image"
              className={clsx({
                [classes.containedImage]: aspect === 0,
              })}
            />
            {/* remove image button  */}
            <button
              type="button"
              className={classes.removeButton}
              data-attrib="remove-button"
              onClick={() => {
                setCroppedImage('');
                setChosenImage(undefined);
                handleChangeFile(null);
              }}
            >
              <TrashIcon />
            </button>
          </div>
        )}
      </div>
    </>
  );
};
